import senators from '../data/student-senate/senators.json'
import stories from '../data/student-senate/state-stories.json'

const STATE_NAMES = {
	AL: 'Alabama',
	AK: 'Alaska',
	AZ: 'Arizona',
	AR: 'Arkansas',
	CA: 'California',
	CO: 'Colorado',
	CT: 'Connecticut',
	DE: 'Delaware',
	DC: 'District of Columbia',
	FL: 'Florida',
	GA: 'Georgia',
	HI: 'Hawaii',
	ID: 'Idaho',
	IL: 'Illinois',
	IN: 'Indiana',
	IA: 'Iowa',
	KS: 'Kansas',
	KY: 'Kentucky',
	LA: 'Louisiana',
	ME: 'Maine',
	MD: 'Maryland',
	MA: 'Massachusetts',
	MI: 'Michigan',
	MN: 'Minnesota',
	MS: 'Mississippi',
	MO: 'Missouri',
	MT: 'Montana',
	NE: 'Nebraska',
	NV: 'Nevada',
	NH: 'New Hampshire',
	NJ: 'New Jersey',
	NM: 'New Mexico',
	NY: 'New York',
	NC: 'North Carolina',
	ND: 'North Dakota',
	OH: 'Ohio',
	OK: 'Oklahoma',
	OR: 'Oregon',
	PA: 'Pennsylvania',
	RI: 'Rhode Island',
	SC: 'South Carolina',
	SD: 'South Dakota',
	TN: 'Tennessee',
	TX: 'Texas',
	UT: 'Utah',
	VT: 'Vermont',
	VA: 'Virginia',
	WA: 'Washington',
	WV: 'West Virginia',
	WI: 'Wisconsin',
	WY: 'Wyoming',
}

const STATE_OPTIONS = Object.entries(STATE_NAMES)
	.map(([id, name]) => ({ id, name }))
	.sort((a, b) => a.name.localeCompare(b.name))

const AVATAR_COLORS = ['#4773b7', '#0497fb', '#c0519e', '#23a043']

function stateVoteResult(stateId) {
	const votes = senators.filter((senator) => senator.state_id === stateId).map((senator) => senator.vote)

	if (votes.length === 0) return null
	if (votes.every((vote) => vote === 'yea')) return 'yea'
	if (votes.every((vote) => vote === 'nay')) return 'nay'
	return 'split'
}

function voteResultLabel(result) {
	if (result === 'yea') return 'unanimous YEA'
	if (result === 'nay') return 'unanimous NAY'
	if (result === 'split') return 'split vote'
	return 'no vote recorded'
}

export default function studentSenate() {
	return {
		selectedState: '',
		expandedSection: 'general',
		states: STATE_OPTIONS,
		tooltip: null,

		init() {
			const paths = this.$refs.mapSvg?.querySelectorAll('.state > path') ?? []

			paths.forEach((path) => {
				const code = path.getAttribute('class')?.toUpperCase().split(/\s+/)[0]

				if (!code || !STATE_NAMES[code]) return

				const count = senators.filter((senator) => senator.state_id === code).length
				const result = stateVoteResult(code)
				path.dataset.state = code
				path.setAttribute('tabindex', '0')
				path.setAttribute('role', 'button')
				path.setAttribute(
					'aria-label',
					`${STATE_NAMES[code]}: ${count} student ${count === 1 ? 'senator' : 'senators'}, ${voteResultLabel(result)}`,
				)
				path.setAttribute('aria-pressed', 'false')
				path.classList.toggle('is-represented', count > 0)
				path.classList.toggle('is-vote-yea', result === 'yea')
				path.classList.toggle('is-vote-nay', result === 'nay')
				path.classList.toggle('is-vote-split', result === 'split')
			})

			this.updateMap()
		},

		get representedStateCount() {
			return new Set(senators.map((senator) => senator.state_id)).size
		},

		get storyCount() {
			return stories.length
		},

		get selectedStateName() {
			return STATE_NAMES[this.selectedState] ?? ''
		},

		get selectedSenators() {
			if (!this.selectedState) return []
			return senators.filter((senator) => senator.state_id === this.selectedState)
		},

		get filteredStories() {
			const list = this.selectedState
				? stories.filter((story) => story.state_id === this.selectedState)
				: stories

			return [...list].sort((a, b) => {
				const dateCmp = (b.published_date || '').localeCompare(a.published_date || '')
				if (dateCmp !== 0) return dateCmp
				return (b.id || 0) - (a.id || 0)
			})
		},

		selectState(code, toggle = false) {
			if (!code) {
				this.clearState()
				return
			}

			this.selectedState = toggle && this.selectedState === code ? '' : code
			this.updateMap()
		},

		clearState() {
			this.selectedState = ''
			this.tooltip = null
			this.updateMap()
		},

		updateMap() {
			const paths = this.$refs.mapSvg?.querySelectorAll('[data-state]') ?? []
			paths.forEach((path) => {
				const selected = path.dataset.state === this.selectedState
				path.classList.toggle('is-selected', selected)
				path.setAttribute('aria-pressed', String(selected))
			})
		},

		mapStateFromEvent(event) {
			return event.target.closest?.('[data-state]')?.dataset.state ?? ''
		},

		handleMapClick(event) {
			const state = this.mapStateFromEvent(event)
			if (state) this.selectState(state, true)
		},

		handleMapKeydown(event) {
			if (!['Enter', ' '].includes(event.key)) return
			const state = this.mapStateFromEvent(event)
			if (!state) return
			event.preventDefault()
			this.selectState(state, true)
		},

		showTooltip(event) {
			const state = this.mapStateFromEvent(event)
			if (!state) {
				this.tooltip = null
				return
			}

			const count = senators.filter((senator) => senator.state_id === state).length
			const result = stateVoteResult(state)
			const bounds = event.target.getBoundingClientRect()
			const pointerX =
				Number.isFinite(event.clientX) && event.clientX > 0 ? event.clientX : bounds.left + bounds.width / 2
			const pointerY = Number.isFinite(event.clientY) && event.clientY > 0 ? event.clientY : bounds.top
			this.tooltip = {
				state,
				name: STATE_NAMES[state],
				count,
				result: voteResultLabel(result),
				x: pointerX + 16,
				y: pointerY - 12,
			}
		},

		moveTooltip(event) {
			if (!this.tooltip) return
			this.tooltip.x = event.clientX + 16
			this.tooltip.y = event.clientY - 12
		},

		hideTooltip() {
			this.tooltip = null
		},

		toggleSection(id) {
			this.expandedSection = this.expandedSection === id ? '' : id
		},

		openAndScroll(id) {
			this.expandedSection = id
			this.$nextTick(() => {
				document.getElementById(`section-${id}`)?.scrollIntoView({
					behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
					block: 'start',
				})
			})
		},

		scrollTo(id) {
			document.getElementById(id)?.scrollIntoView({
				behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
				block: 'start',
			})
		},

		initials(name) {
			return name
				.split(' ')
				.map((part) => part[0])
				.join('')
				.slice(0, 2)
				.toUpperCase()
		},

		avatarColor(index) {
			return AVATAR_COLORS[index % AVATAR_COLORS.length]
		},

		formatDate(date) {
			if (!date) return ''
			return new Intl.DateTimeFormat('en-US', {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
				timeZone: 'UTC',
			}).format(new Date(`${date}T00:00:00Z`))
		},
	}
}
